import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, MoreHorizontal, Trophy, XCircle, Trash2, Sparkles, FileText, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtCompact, fmtDate, fmtPct } from "@/lib/format";
import { stageColor, stageLabel } from "@/lib/stages";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { HealthIndicator, Skeleton, UserAvatar, CompanyLogo } from "@/components/common";
import { OverviewTab } from "@/components/opportunity/OverviewTab";
import { RequirementsTab } from "@/components/opportunity/RequirementsTab";
import { SolutionTab } from "@/components/opportunity/SolutionTab";
import { CalculationTab } from "@/components/opportunity/CalculationTab";
import { QuoteTab } from "@/components/opportunity/QuoteTab";
import { FilesTab } from "@/components/opportunity/FilesTab";
import { ActivityTab } from "@/components/opportunity/ActivityTab";
import { WonDialog } from "@/components/opportunity/WonDialog";
import { LostDialog } from "@/components/opportunity/LostDialog";
import { cn } from "@/lib/utils";

const TABS = ["overview", "requirements", "solution", "calculation", "quote", "files", "activity"];
const TAB_LABEL = { overview: "Overview", requirements: "Requirements", solution: "Solution", calculation: "Calculation", quote: "Quote", files: "Files", activity: "Activity" };

export default function OpportunityPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "overview";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [wonOpen, setWonOpen] = useState(params.get("handover") === "1");
  const [lostOpen, setLostOpen] = useState(false);
  const navigate = useNavigate();

  const reload = useCallback(async () => {
    try {
      const r = await api.get(`/opportunities/${id}`);
      setData(r.data);
      return r.data;
    } catch (e) {
      setError(e);
      return null;
    }
  }, [id]);

  useEffect(() => {
    setData(null);
    reload();
  }, [reload]);

  const setTab = (t) => {
    const p = new URLSearchParams(params);
    p.set("tab", t);
    p.delete("handover");
    setParams(p, { replace: true });
  };

  const changeStage = async (stage) => {
    if (stage === "won") return markWon();
    if (stage === "lost") return setLostOpen(true);
    try {
      await api.patch(`/opportunities/${id}`, { stage });
      toast.success(`Stage: ${stageLabel(stage, data?.stages)}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
    return undefined;
  };

  const markWon = async () => {
    try {
      await api.post(`/opportunities/${id}/won`);
      toast.success("Marked as Won");
      await reload();
      setWonOpen(true);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this opportunity and all its data?")) return;
    await api.delete(`/opportunities/${id}`);
    toast.success("Deleted");
    navigate("/pipeline");
  };

  if (error) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-20 text-center">
        <h1 className="text-2xl font-semibold">Opportunity not found</h1>
        <Button asChild variant="outline" className="mt-4"><Link to="/pipeline">Back to pipeline</Link></Button>
      </div>
    );
  }

  const o = data?.opportunity;
  const stages = data?.stages || [];
  const activeSolution = data?.solutions?.find((s) => s.is_active);
  const counts = {
    requirements: data?.requirements?.length || 0,
    quote: data?.quotes?.length || 0,
    files: data?.files?.length || 0,
    solution: data?.solutions?.length || 0,
  };
  const closed = o && o.status !== "open";

  const primaryAction = () => {
    if (!o || closed) return null;
    if (["new", "discovery"].includes(o.stage) && !activeSolution) {
      return <Button className="gap-2" onClick={() => setTab("solution")} data-testid="opportunity-primary-action"><Sparkles className="h-4 w-4" /> Generate solution proposal</Button>;
    }
    if (["new", "discovery", "solution_design", "calculation"].includes(o.stage)) {
      return <Button className="gap-2" onClick={() => setTab("quote")} data-testid="opportunity-primary-action"><FileText className="h-4 w-4" /> Generate quote</Button>;
    }
    return <Button className="gap-2" onClick={markWon} data-testid="opportunity-primary-action"><Trophy className="h-4 w-4" /> Mark as Won</Button>;
  };

  const stageIdx = stages.findIndex((s) => s.key === o?.stage);
  const flowStages = stages.filter((s) => !["won", "lost"].includes(s.key));

  return (
    <div className="mx-auto max-w-[1200px] px-5 pt-6 sm:px-8 lg:px-10">
      <nav className="flex items-center gap-1.5 text-[12.5px] text-muted-foreground" aria-label="Breadcrumb">
        <Link to="/pipeline" className="inline-flex items-center gap-1.5 hover:text-foreground" data-testid="opportunity-back-link"><ArrowLeft className="h-3.5 w-3.5" /> Opportunities</Link>
        {o ? (<><span className="text-border">/</span><Link to={`/customers/${o.account_id}`} className="hover:text-foreground">{o.account_name}</Link></>) : null}
      </nav>
      {!o ? (
        <div className="mt-5 space-y-4"><Skeleton className="h-12 w-2/3" /><Skeleton className="h-6 w-1/2" /><Skeleton className="h-64" /></div>
      ) : (
        <>
          <div className="mt-5 flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <CompanyLogo name={o.account_name} domain={o.account_website} size="lg" className="mt-1 hidden sm:flex" />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <Link to={`/customers/${o.account_id}`} className="eyebrow hover:text-foreground">{o.account_name}</Link>
                  {o.type === "tender" ? <span className="rounded-md border border-black/[0.08] px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-wide text-muted-foreground">Tender</span> : null}
                </div>
                <h1 className="mt-1 text-[30px] font-semibold leading-[1.08] tracking-[-0.03em] sm:text-[36px]" data-testid="opportunity-title">
                  <span className="sr-only">{o.account_name} – </span>{o.name}
                </h1>
                <div className="mt-3.5 flex flex-wrap items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button type="button" className="inline-flex h-8 items-center gap-2 rounded-full border border-black/[0.09] bg-white px-3 text-[12.5px] font-medium shadow-[0_1px_2px_hsl(30_12%_18%/0.04)] hover:border-black/[0.18] focus-visible:ring-2 focus-visible:ring-ring/40" data-testid="opportunity-stage-select">
                        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: stageColor(o.stage) }} />
                        {stageLabel(o.stage, stages)} <ChevronDown className="h-3 w-3 text-muted-foreground" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {stages.map((s) => (
                        <DropdownMenuItem key={s.key} onClick={() => changeStage(s.key)} data-testid={`stage-option-${s.key}`} className="gap-2">
                          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: stageColor(s.key) }} /> {s.label}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <span className="inline-flex h-8 items-center gap-2 rounded-full border border-black/[0.07] bg-[hsl(40_10%_96%)] px-3 text-[12.5px] text-muted-foreground">
                    <UserAvatar name={o.owner_name} initials={o.owner_initials} color="#111" size="xs" /> {o.owner_name}
                  </span>
                  <span className="inline-flex h-8 items-center gap-1.5 rounded-full border border-black/[0.07] bg-[hsl(40_10%_96%)] px-3 text-[12.5px] text-muted-foreground">
                    <CalendarClock className="h-3.5 w-3.5" /> Decision <span className="font-medium tabular text-foreground">{o.expected_close_date ? fmtDate(o.expected_close_date) : "–"}</span>
                  </span>
                  <span className="inline-flex h-8 items-center rounded-full border border-black/[0.07] bg-[hsl(40_10%_96%)] px-3 text-[12.5px]">
                    <HealthIndicator health={o.health} reason={o.reason} />
                  </span>
                </div>
              </div>
            </div>
            <div className="flex shrink-0 flex-col items-start gap-3 lg:items-end">
              <div className="flex items-baseline gap-4 lg:flex-col lg:items-end lg:gap-0">
                <span className="font-display text-[30px] font-semibold leading-none tabular tracking-[-0.03em]" data-testid="opportunity-value">{fmtCompact(o.value)}</span>
                <span className="text-[12.5px] text-muted-foreground">Margin <span className={cn("font-medium tabular", o.gm_pct !== null && o.gm_pct < 30 ? "text-[hsl(var(--status-warn))]" : "text-foreground")} data-testid="opportunity-margin">{o.gm_pct !== null && o.gm_pct !== undefined ? fmtPct(o.gm_pct) : "–"}</span></span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" className="gap-2 rounded-full" onClick={async () => { try { const r = await api.post("/workspace", { opportunity_id: id }); navigate(`/workspace/${r.data.session.id}`); } catch (e) { toast.error(errMsg(e)); } }} data-testid="opportunity-open-workspace-button"><Sparkles className="h-4 w-4 text-primary" /> AI Workspace</Button>
                {primaryAction()}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild><Button variant="outline" size="icon" data-testid="opportunity-more-button" aria-label="More actions"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!closed && <DropdownMenuItem onClick={markWon} className="gap-2" data-testid="opportunity-mark-won"><Trophy className="h-4 w-4" /> Mark as Won</DropdownMenuItem>}
                    {!closed && <DropdownMenuItem onClick={() => setLostOpen(true)} className="gap-2" data-testid="opportunity-mark-lost"><XCircle className="h-4 w-4" /> Mark as Lost</DropdownMenuItem>}
                    {closed && <DropdownMenuItem onClick={() => setWonOpen(true)} className="gap-2" data-testid="opportunity-open-handover"><Trophy className="h-4 w-4" /> View handover</DropdownMenuItem>}
                    {closed && <DropdownMenuItem onClick={async () => { await api.post(`/opportunities/${id}/reopen`); reload(); }} className="gap-2">Reopen</DropdownMenuItem>}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={remove} className="gap-2 text-destructive" data-testid="opportunity-delete"><Trash2 className="h-4 w-4" /> Delete</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>

          {/* Stage progress */}
          {!closed && (
            <ol className="mt-6 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${flowStages.length}, minmax(0, 1fr))` }} data-testid="opportunity-stage-progress" aria-label="Stage progress">
              {flowStages.map((s, i) => {
                const done = i < stageIdx;
                const current = i === stageIdx;
                return (
                  <li key={s.key}>
                    <button
                      type="button"
                      onClick={() => changeStage(s.key)}
                      title={`Move to ${s.label}`}
                      data-testid={`opportunity-stage-step-${s.key}`}
                      className="group w-full text-left focus-visible:outline-none"
                    >
                      <span className={cn("block h-[5px] w-full rounded-full transition-colors", current ? "bg-primary" : done ? "bg-foreground/80" : "bg-black/[0.08] group-hover:bg-black/[0.16]")} />
                      <span className={cn("mt-1.5 hidden text-[11px] font-medium sm:block", current ? "text-primary" : done ? "text-foreground/80" : "text-muted-foreground/70")}>{s.label}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          )}

          {o.status === "won" && <div className="mt-5 rounded-xl border border-[hsl(var(--status-ok)/0.3)] bg-[hsl(var(--status-ok)/0.06)] px-4 py-2.5 text-sm" data-testid="opportunity-won-banner">Won on {fmtDate(o.won_at, { year: true })}. {o.visma_order_no ? `Order ${o.visma_order_no} sent to Visma.` : "Handover is ready."} <button type="button" className="ml-2 font-medium underline underline-offset-2" onClick={() => setWonOpen(true)}>Open handover</button></div>}
          {o.status === "lost" && <div className="mt-5 rounded-xl border border-[hsl(var(--status-bad)/0.3)] bg-[hsl(var(--status-bad)/0.06)] px-4 py-2.5 text-sm" data-testid="opportunity-lost-banner">Lost on {fmtDate(o.lost_at, { year: true })} – {o.lost_reason}. {o.lost_note}</div>}

          <div className="sticky top-0 z-10 -mx-5 mt-6 border-b border-black/[0.06] bg-background px-5 sm:-mx-8 sm:px-8 lg:-mx-10 lg:px-10">
            <div className="flex gap-1 overflow-x-auto scrollbar-thin" role="tablist">
              {TABS.map((t) => (
                <button key={t} type="button" role="tab" aria-selected={tab === t} onClick={() => setTab(t)} data-testid={`opportunity-tab-${t}`} className={cn("relative -mb-px flex items-center gap-1.5 whitespace-nowrap px-3 py-3 text-[13.5px] font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:text-foreground", tab === t && "text-foreground after:absolute after:inset-x-3 after:-bottom-px after:h-[2px] after:rounded-full after:bg-primary")}>
                  {TAB_LABEL[t]}
                  {counts[t] ? <span className={cn("rounded-full px-1.5 text-[11px] tabular", tab === t ? "bg-primary/10 text-primary" : "bg-black/[0.06] text-muted-foreground")}>{counts[t]}</span> : null}
                </button>
              ))}
            </div>
          </div>

          <div className="py-6 pb-10 fade-in" key={tab}>
            {tab === "overview" && <OverviewTab data={data} reload={reload} setTab={setTab} />}
            {tab === "requirements" && <RequirementsTab data={data} reload={reload} setTab={setTab} />}
            {tab === "solution" && <SolutionTab data={data} reload={reload} setTab={setTab} />}
            {tab === "calculation" && <CalculationTab data={data} reload={reload} setTab={setTab} />}
            {tab === "quote" && <QuoteTab data={data} reload={reload} setTab={setTab} />}
            {tab === "files" && <FilesTab data={data} reload={reload} setTab={setTab} />}
            {tab === "activity" && <ActivityTab data={data} reload={reload} />}
          </div>

          <WonDialog opportunityId={id} open={wonOpen} onOpenChange={(v) => { setWonOpen(v); if (!v) { const p = new URLSearchParams(params); p.delete("handover"); setParams(p, { replace: true }); reload(); } }} />
          <LostDialog opportunity={o} open={lostOpen} onOpenChange={setLostOpen} onDone={reload} />
        </>
      )}
    </div>
  );
}
